import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, ButtonBase, CircularProgress, Dialog, InputBase, Typography } from "@mui/material";
import { RiErrorWarningLine, RiSearchLine } from "@remixicon/react";
import { searchPeople, PHONE_ALIASES } from "../../services/dataService";
import { focusRing, tokens } from "../../utils/figmaTokens";
import InitialsAvatar from "../kit/InitialsAvatar";
import Kbd from "../kit/Kbd";

// Ctrl+K people search (spec 024 D4/T9), drawn as Figma's Global Search Panel (047 D12).
// Firestore has no substring search — searchPeople does exact-phone /
// prefix-name-or-email, so the empty state below says exactly that instead of
// implying a "contains" search that isn't happening.
const getPhone = (person) => PHONE_ALIASES.map((f) => person[f]).find(Boolean);

// searchPeople returns students and teachers only; each gets a group.
const GROUPS = [
  { role: "student", caption: "Students", type: "Student" },
  { role: "teacher", caption: "Teachers", type: "Teacher" },
];

const FOOTER_KEYS = [
  ["↑↓", "navigate"],
  ["↵", "open"],
  ["esc", "close"],
];

// The panel's no-results and error body (Figma "No results" 10660:7121).
const Message = ({ ring, color, Icon, title, hint, role }) => (
  <Box role={role} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "6px", px: "24px", py: "40px", textAlign: "center" }}>
    <Box sx={{ display: "flex", p: "14px", borderRadius: "999px", bgcolor: ring, color }}>
      <Icon size={22} />
    </Box>
    <Typography variant="bodyMediumStrong" sx={{ color: tokens.text.primary }}>
      {title}
    </Typography>
    {hint && (
      <Typography variant="bodySmall" sx={{ color: tokens.text.tertiary }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const SearchOverlay = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rowRefs = useRef([]);
  const listRef = useRef(null);

  // Reset so a reopen doesn't flash yesterday's search.
  useEffect(() => {
    if (!open) {
      setTerm("");
      setResults([]);
      setLoading(false);
      setError(false);
    }
  }, [open]);

  useEffect(() => {
    const query = term.trim();
    if (!query) {
      setResults([]);
      setLoading(false);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    const handle = setTimeout(async () => {
      try {
        const found = await searchPeople(query);
        if (!cancelled) setResults(found);
      } catch (e) {
        // A permission error, a missing index, or an offline moment is not
        // "no one matches" — that's a claim about the data this branch has
        // no basis for (spec 024 review). Keep it a distinct error state.
        if (!cancelled) {
          setResults([]);
          setError(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [term]);

  // The rows in the order they are drawn; the arrow keys and Enter walk this order.
  const groups = GROUPS.map((g) => ({ ...g, people: results.filter((r) => r.role === g.role) })).filter(
    (g) => g.people.length > 0
  );
  const rows = groups.flatMap((g) => g.people);
  const offsets = groups.map((g, i) => groups.slice(0, i).reduce((n, x) => n + x.people.length, 0));

  // New results start at the top, where the highlight (and Enter) points.
  useEffect(() => {
    setHighlight(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [results]);

  useEffect(() => {
    rowRefs.current[highlight]?.scrollIntoView({ block: "nearest" });
  }, [highlight]);

  const goToPerson = (person) => {
    // A student (044 D29) and a tutor (047 step 4) each have their own page: go straight
    // there. The lists' ?open= redirects stay for older links, but they would load the list first.
    navigate(person.role === "teacher" ? `/tutors/${person.id}` : `/students/${person.id}`);
    onClose();
  };

  const handleKeyDown = (e) => {
    if ((e.key === "ArrowDown" || e.key === "ArrowUp") && rows.length > 0) {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : rows.length - 1;
      setHighlight((h) => (h + step) % rows.length);
    } else if (e.key === "Enter" && rows[highlight]) {
      e.preventDefault();
      goToPerson(rows[highlight]);
    }
  };

  const query = term.trim();
  const showEmptyState = query && !loading && !error && results.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={{ "& .MuiDialog-container": { alignItems: "flex-start" } }}
      slotProps={{
        paper: {
          "aria-label": "Search people",
          sx: {
            width: 720,
            maxWidth: "calc(100% - 32px)",
            m: "10vh 16px 16px",
            borderRadius: "16px",
            border: `1px solid ${tokens.border.subtle}`,
            boxShadow: tokens.shadow.panel,
            overflow: "hidden",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          pl: "20px",
          pr: "16px",
          py: "16px",
          borderBottom: `1px solid ${tokens.border.subtle}`,
        }}
      >
        <RiSearchLine size={20} color={tokens.icon.secondary} />
        <InputBase
          autoFocus
          fullWidth
          placeholder="Search by name, email, or phone…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          inputProps={{ "aria-label": "Search people" }}
          sx={{
            typography: "bodyMedium",
            color: tokens.text.primary,
            "& input::placeholder": { color: tokens.text.tertiary, opacity: 1 },
          }}
        />
        {loading && <CircularProgress size={16} />}
        <Kbd>Esc</Kbd>
      </Box>

      {error ? (
        <Message role="alert" ring={tokens.error.bg} color={tokens.error.ink} Icon={RiErrorWarningLine} title="Search failed — try again." />
      ) : showEmptyState ? (
        <Message
          ring={tokens.surface.muted}
          color={tokens.icon.secondary}
          Icon={RiSearchLine}
          title={`No results for "${query}"`}
          hint="Search matches the start of names and emails, or an exact phone number."
        />
      ) : (
        groups.length > 0 && (
          <Box ref={listRef} sx={{ maxHeight: "min(60vh, 560px)", overflowY: "auto", pt: "4px", pb: "8px" }}>
            {groups.map((g, gi) => (
              <Box key={g.role} sx={{ display: "flex", flexDirection: "column", gap: "2px", px: "8px", pt: "10px", pb: "6px" }}>
                <Typography variant="labelCaption" component="div" sx={{ px: "12px", pt: "4px", pb: "6px", color: tokens.text.tertiary }}>
                  {g.caption} · {g.people.length}
                </Typography>
                {g.people.map((person, i) => {
                  const index = offsets[gi] + i;
                  const on = index === highlight;
                  return (
                    <ButtonBase
                      key={person.id}
                      ref={(el) => {
                        rowRefs.current[index] = el;
                      }}
                      onClick={() => goToPerson(person)}
                      onMouseMove={() => setHighlight(index)}
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        px: "12px",
                        py: "10px",
                        borderRadius: "10px",
                        textAlign: "left",
                        // 047 D70: the chosen row must read against the white dialog; `hover`
                        // (#fafafb) is 1.04:1 and vanishes.
                        bgcolor: on ? tokens.brand.subtle : "transparent",
                        ...focusRing,
                      }}
                    >
                      <InitialsAvatar name={person.name || "Unknown"} colorKey={person.id} size={32} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="bodySmallStrong" noWrap sx={{ color: tokens.text.primary }}>
                          {person.name || "Unknown"}
                        </Typography>
                        <Typography variant="labelMedium" component="div" noWrap sx={{ color: tokens.text.tertiary }}>
                          {[getPhone(person), person.email].filter(Boolean).join(" · ")}
                        </Typography>
                      </Box>
                      <Typography variant="labelCaption" sx={{ color: tokens.text.tertiary }}>
                        {g.type}
                      </Typography>
                      {on && <Kbd>↵</Kbd>}
                    </ButtonBase>
                  );
                })}
              </Box>
            ))}
          </Box>
        )
      )}

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "14px", px: "20px", py: "10px", bgcolor: tokens.surface.sunken }}>
        {FOOTER_KEYS.map(([key, label]) => (
          <Box key={label} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Kbd>{key}</Kbd>
            <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
              {label}
            </Typography>
          </Box>
        ))}
        <Box sx={{ flex: 1 }} />
        <Typography variant="labelMedium" sx={{ color: tokens.text.tertiary }}>
          Students · teachers
        </Typography>
      </Box>
    </Dialog>
  );
};

export default SearchOverlay;
